import { useQuery, useReactiveVar } from '@apollo/client';
import React from 'react';
import { userPrivileges } from '../../../apollo/cache';
import PrivilegeNames from '../../../constants/privilegeNames';
import { Page } from '../../layout';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import ResponsiveTabMenu from '../../shared/components/ResponsiveTabMenu';
import { useTabExistCheck } from '../../shared/hooks/useTabExistCheck';
import GET_CURRENT_USER from '../../shared/queries/currentUserQuery';
import { getActiveTabDisplayName } from '../../shared/services/getActiveTabDisplayNameService';
import { getPageTitle } from '../../shared/services/pageTitleService';
import useUriFragment from './../../shared/hooks/useUriFragment';
import EmailPreferences from './EmailPreferences';
import AppraiserProfile from './AppraiserProfile';

const AccountPage = () => {
  const privileges = useReactiveVar(userPrivileges);

  const tabs = getTabs(privileges);
  const [activeTab, setActiveTab] = useUriFragment(tabs);

  useTabExistCheck(tabs, activeTab);
  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);

  const pageTitle = getPageTitle([activeTabDisplayName]);
  const breadcrumbPathItems = [
    { name: 'Account', url: '/account' },
    { name: activeTabDisplayName, url: '' }
  ];

  const { data } = useQuery(GET_CURRENT_USER);

  const userName = data && data.currentUser.Name;
  const title =
    activeTab === TAB_TYPES.AppraiserProfile ? `Appraiser: ${userName}` : `Account: ${userName}`;

  const handleTabChange = newType => {
    setActiveTab(newType);
  };

  const getActiveTabContent = tabType => {
    switch (tabType) {
      case TAB_TYPES.AppraiserProfile:
        return <AppraiserProfile />;
      case TAB_TYPES.EmailPreferences:
        return <EmailPreferences />;
      default:
        return '';
    }
  };

  return (
    <Page title={pageTitle} className='account-page'>
      <Breadcrumb pathItems={breadcrumbPathItems} />
      <p className='bold'>{title}</p>
      <ResponsiveTabMenu activeTab={activeTab} tabs={tabs} onChange={handleTabChange} />
      {data && <div>{getActiveTabContent(activeTab)}</div>}
    </Page>
  );
};

const getTabs = privileges => {
  const tabs = [];

  if (privileges.includes(PrivilegeNames.AppraiserProfile)) {
    tabs.push({
      value: TAB_TYPES.AppraiserProfile,
      displayName: 'Appraiser Profile'
    });
  }

  if (privileges.includes(PrivilegeNames.EmailPreferences)) {
    tabs.push({
      value: TAB_TYPES.EmailPreferences,
      displayName: 'Email Preferences'
    });
  }

  return tabs;
};

const TAB_TYPES = {
  EmailPreferences: 'email-preferences',
  AppraiserProfile: 'appraiser-profile'
};

export default AccountPage;
